import * as React from 'react';
import cx from 'classnames';
import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { TextFieldProps, useTextFieldController } from './text-field.controller';
import { Flex } from '@common/components/Flex';
import { getHeadingAlignmentStyle, getHeadingPositionOffset } from '../field-heading.utils';
import { FieldContainer } from '../field-container';
import { Hint } from '@common/components/hint';
import { useFieldContentStyle } from '../content-style.utils';
import { DatasheetComponentTextStyle } from '@common/dto/datasheet/component-settings/text-style';
import { useForceDisabled } from '../../context';

import './text-field.styles.scss';

export const TextField: React.FC<TextFieldProps> = React.memo((props) => {
  const { datasheetElement } = props;

  const {
    textFieldValue,
    headingPlacedHorizontally,
    headingReference,
    headingSize,
    headingPosition,
    handleTextFieldValueChange,
    handleBlur,
    handleFocus,
    isGTR,
    isFocused,
    fieldWidth,
    fieldHeight,
    containerRef,
    fieldContainerRef,
    inputRef,
    handleTabNavigation,
    isEditable,
  } = useTextFieldController(props);

  const forceDisabled = useForceDisabled();

  const {
    contentColor,
    contentAlign,
    contentFontFamily,
    contentFontSize,
    contentTextStyles,
    formControlStyle,
  } = useFieldContentStyle(datasheetElement.original);


  const headingAlignmentStyle = getHeadingAlignmentStyle({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    fieldWidth,
  });

  const headingPositionOffset = getHeadingPositionOffset({
    headingPosition,
    headAlign: datasheetElement.original.headalign,
    headingSize,
    fieldWidth,
    fieldHeight,
  });

  const fieldIsEmpty = (() => { const v = (datasheetElement.original as any).field?.name ?? (datasheetElement.original as any).field?.value; return v == null || String(v).trim() === ''; })();

  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={datasheetElement.original.showfieldtype}
      withText={datasheetElement.original.headdisplay}
      text={datasheetElement.original.headtext} 
      img={datasheetElement.original.field.imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  return (
    <DatasheetItem
      style={{
        top: datasheetElement.coordinates.y,
        left: datasheetElement.coordinates.x,
      }}
    >
      {labelComponent}
      <Flex alignItems="center" style={{ position: 'relative' }}>
        <FieldContainer
          ref={fieldContainerRef}
          fieldId={datasheetElement.original.field.fieldid ?? datasheetElement.original.field.id}
          width={fieldWidth}
          height={fieldHeight}
          showBorder={datasheetElement.original.showborder}
          required={datasheetElement.original.required}
          isEmpty={fieldIsEmpty}
          className={cx({
            'DatasheetsResultTextField__gtr-field': isGTR,
            'DatasheetsResultTextField__gtr-field--expanded': isGTR && isFocused,
            'DatasheetsResultTextField__resizable': isFocused,
          })}
        >
          <div
            ref={containerRef}
            className={cx('DatasheetsResultTextField__container', {
              'DatasheetsResultTextField__container--gtr': isGTR,
            })}
          >
            {isGTR ? (
              isEditable ? (
                <textarea
                  ref={inputRef}
                  className="DatasheetsResultTextField__textarea"
                  value={forceDisabled ? '' : textFieldValue}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onChange={handleTextFieldValueChange}
                  onKeyDown={handleTabNavigation}
                  style={{
                    resize: 'none',
                    overflow: isFocused ? 'auto' : 'hidden',
                    fontFamily: contentFontFamily,
                    fontSize: contentFontSize,
                    color: contentColor,
                    textAlign: contentAlign,
                    fontWeight: contentTextStyles.includes(DatasheetComponentTextStyle.bold) ? 'bold' : undefined,
                    fontStyle: contentTextStyles.includes(DatasheetComponentTextStyle.italic) ? 'italic' : undefined,
                    textDecoration: contentTextStyles.includes(DatasheetComponentTextStyle.underline) ? 'underline' : undefined,
                  }}
                />
              ) : (
                <div
                  className={cx('DatasheetsResultTextField__display', {
                    'DatasheetsResultTextField__display--expanded': isFocused,
                  })}
                  style={formControlStyle}
                  tabIndex={0}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                >
                  {forceDisabled ? '' : textFieldValue}
                </div>
              )
            ) : (
              <textarea
                ref={inputRef}
                className="DatasheetsResultTextField__textarea"
                value={forceDisabled ? '' : textFieldValue}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={isEditable ? handleTextFieldValueChange : undefined}
                onKeyDown={isEditable ? handleTabNavigation : undefined}
                disabled={!isEditable}
                readOnly={!isEditable}
                style={formControlStyle}
              />
            )}
          </div>
        </FieldContainer>
        {datasheetElement.original.helptext && (
          <Hint description={datasheetElement.original.helptext} />
        )}
      </Flex>
    </DatasheetItem>
  );
});